import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import supabase from '../services/supabaseClient';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, userData: { name: string; company: string }) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithOAuth: (provider: 'google' | 'github' | 'discord' | 'microsoft') => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isWhitelisted: boolean | null;
  checkWhitelistStatus: (userId?: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isWhitelisted, setIsWhitelisted] = useState<boolean | null>(null);
  
  // Cache to prevent duplicate checks
  const [checkingWhitelist, setCheckingWhitelist] = useState(false);
  const [lastCheckedUserId, setLastCheckedUserId] = useState<string | null>(null);
  const lastCheckedUserIdRef = useRef<string | null>(null);

  const checkWhitelistStatus = useCallback(async (userId?: string): Promise<boolean> => {
    const userIdToCheck = userId || user?.id;
    
    if (!userIdToCheck) {
      setIsWhitelisted(false);
      return false;
    }

    // If we're already checking for this user, return current status
    if (checkingWhitelist && lastCheckedUserId === userIdToCheck) {
      console.log('Whitelist check already in progress for user:', userIdToCheck);
      return isWhitelisted === true;
    }

    // If we already checked this user and have a result, return it
    if (lastCheckedUserId === userIdToCheck && isWhitelisted !== null) {
      console.log('Using cached whitelist status for user:', userIdToCheck, isWhitelisted);
      return isWhitelisted === true;
    }

    setCheckingWhitelist(true);
    setLastCheckedUserId(userIdToCheck);
    lastCheckedUserIdRef.current = userIdToCheck;

    try {
      console.log('Checking whitelist status for user:', userIdToCheck);
      
      // Add a timeout to the query (5 seconds max - increased for reliability)
      const queryPromise = supabase
        .from('user_profiles')
        .select('is_whitelisted')
        .eq('id', userIdToCheck)
        .maybeSingle(); // Use maybeSingle() to handle missing profiles gracefully
      
      let timeoutId: NodeJS.Timeout;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Query timeout after 5 seconds')), 5000);
      });
      
      let queryResult: any;
      try {
        queryResult = await Promise.race([queryPromise, timeoutPromise]);
        clearTimeout(timeoutId!);
      } catch (raceError: any) {
        clearTimeout(timeoutId!);
        if (raceError?.message?.includes('timeout')) {
          console.warn('Whitelist query timed out - likely RLS issue or network problem');
          setIsWhitelisted(false);
          setCheckingWhitelist(false);
          return false;
        }
        throw raceError;
      }

      const { data, error } = queryResult;

      // If profile doesn't exist or there's an error, default to false (not whitelisted)
      if (error) {
        // If it's a "not found" error (PGRST116), treat as not whitelisted
        if (error.code === 'PGRST116' || error.message?.includes('0 rows')) {
          console.log('Profile not found, defaulting to not whitelisted');
          setIsWhitelisted(false);
          setCheckingWhitelist(false);
          return false;
        }
        // For other errors, also default to false
        console.warn('Whitelist check error:', error.message, error.code);
        setIsWhitelisted(false);
        setCheckingWhitelist(false);
        return false;
      }

      if (!data) {
        console.log('No data returned, profile likely does not exist');
        setIsWhitelisted(false);
        setCheckingWhitelist(false);
        return false;
      }

      const whitelisted = data.is_whitelisted === true;
      console.log('Whitelist status:', whitelisted, 'for user:', userIdToCheck);
      setIsWhitelisted(whitelisted);
      setCheckingWhitelist(false);
      return whitelisted;
    } catch (error: any) {
      console.error('Error checking whitelist (catch block):', error);
      // Always set a boolean value, never leave as null
      setIsWhitelisted(false);
      setCheckingWhitelist(false);
      return false;
    }
  }, [user?.id, checkingWhitelist, lastCheckedUserId, isWhitelisted]);

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        setSession(data.session);
        setUser(data.session?.user ?? null);
        
        // Check whitelist status if user exists
        if (data.session?.user) {
          // Reset cache for new user
          if (lastCheckedUserIdRef.current !== data.session.user.id) {
            setLastCheckedUserId(null);
            lastCheckedUserIdRef.current = null;
            setIsWhitelisted(null);
          }
          await checkWhitelistStatus(data.session.user.id);
        } else {
          // No user, set whitelisted to null (not authenticated)
          setIsWhitelisted(null);
          setLastCheckedUserId(null);
          lastCheckedUserIdRef.current = null;
          setCheckingWhitelist(false);
        }
      } catch (error) {
        console.error('Error checking user session:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Check whitelist status when auth state changes - use session user directly
        // Only check if user changed or we don't have a status yet
        if (session?.user) {
          // Reset cache when user changes
          if (lastCheckedUserIdRef.current !== session.user.id) {
            setLastCheckedUserId(null);
            lastCheckedUserIdRef.current = null;
            setIsWhitelisted(null);
          }
          await checkWhitelistStatus(session.user.id);
        } else {
          // No user, reset everything
          setIsWhitelisted(null);
          setLastCheckedUserId(null);
          lastCheckedUserIdRef.current = null;
          setCheckingWhitelist(false);
        }
      }
    );

    return () => subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - checkWhitelistStatus is stable via useCallback

  const signUp = async (email: string, password: string, userData: { name: string; company: string }) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            company: userData.company,
          },
        },
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const signInWithOAuth = async (provider: 'google' | 'github' | 'discord' | 'microsoft') => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider as any,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithOAuth,
    resetPassword,
    logout,
    isAuthenticated: !!user,
    isWhitelisted,
    checkWhitelistStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
